import { useState, useEffect } from 'react';
import { getMediaFilesByFileId } from '../services/api/familypal';

// In-memory cache: fileId -> { url, thumbnailUrl, expiresAt }
interface CachedUrls {
  url: string;
  thumbnailUrl: string | null;
  expiresAt: number;
}

const urlCache = new Map<string, CachedUrls>();

// Cache for 50 minutes (presigned URLs expire in 60)
const CACHE_DURATION_MS = 50 * 60 * 1000;

async function fetchFileUrls(fileId: string): Promise<CachedUrls | null> {
  // Check cache first
  const cached = urlCache.get(fileId);
  if (cached && cached.expiresAt > Date.now()) {
    return cached;
  }

  try {
    const response = await getMediaFilesByFileId({
      path: { file_id: fileId },
    } as any);

    if (response.error || !response.data) {
      console.warn('Failed to fetch profile photo URL:', response.error);
      return null;
    }

    const result = response.data as any;
    const url = result?.uri || result?.download_url || result?.downloadUrl;
    const thumbnailUrl = result?.thumbnail_uri || result?.thumbnailUri || null;

    if (url) {
      const entry: CachedUrls = {
        url,
        thumbnailUrl,
        expiresAt: Date.now() + CACHE_DURATION_MS,
      };
      urlCache.set(fileId, entry);
      return entry;
    }
    return null;
  } catch (err) {
    console.error('Failed to fetch profile photo URL:', err);
    return null;
  }
}

/**
 * Hook that fetches presigned download URLs for a profile photo file.
 * When thumbnail is true, returns the thumbnail URL if available.
 * Caches URLs for 50 minutes (they expire in 60).
 */
export function useProfilePhotoUrl(fileId?: string | null, thumbnail?: boolean): string | null {
  const [url, setUrl] = useState<string | null>(() => {
    if (!fileId) return null;
    const cached = urlCache.get(fileId);
    if (cached && cached.expiresAt > Date.now()) {
      return thumbnail ? (cached.thumbnailUrl || cached.url) : cached.url;
    }
    return null;
  });

  useEffect(() => {
    if (!fileId) {
      setUrl(null);
      return;
    }

    let cancelled = false;

    fetchFileUrls(fileId).then((result) => {
      if (!cancelled && result) {
        const finalUrl = thumbnail ? (result.thumbnailUrl || result.url) : result.url;
        setUrl(finalUrl);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [fileId, thumbnail]);

  return url;
}

/**
 * Utility function to get a cached URL synchronously, or null if not cached.
 * Use this when you need URLs for multiple items without hooks.
 */
export function getCachedProfilePhotoUrl(fileId: string, thumbnail?: boolean): string | null {
  const cached = urlCache.get(fileId);
  if (cached && cached.expiresAt > Date.now()) {
    return thumbnail ? (cached.thumbnailUrl || cached.url) : cached.url;
  }
  return null;
}

/**
 * Prefetch profile photo URLs for a list of file IDs.
 * Returns a map of fileId -> url.
 */
export async function prefetchProfilePhotoUrls(
  fileIds: string[],
  thumbnail?: boolean
): Promise<Map<string, string>> {
  const results = new Map<string, string>();

  await Promise.all(
    fileIds.map(async (fileId) => {
      const cached = await fetchFileUrls(fileId);
      if (cached) {
        const url = thumbnail ? (cached.thumbnailUrl || cached.url) : cached.url;
        results.set(fileId, url);
      }
    })
  );

  return results;
}

export default useProfilePhotoUrl;
